import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, type Type } from '@angular/core';
import type { FormlyFieldConfig } from '@ngx-formly/core';

import { isEmptyValue } from '../../../utils/empty-value.util';
import { DdValueDirective, LabelListComponent } from '../../label-list.component';
import { StatusBadgeComponent } from '../../status-badge.component';
import { CompositeDisplayComponent } from './displays/composite-display.component';
import { resolveReadonlyDisplay } from './readonly-display.registry';
// From `readonly.types` directly, not through the registry's re-export: this is read mode's
// shared vocabulary and that file is where it lives (project convention, types in a
// `*.types.ts`). One import hop fewer for anyone following the contract back to its definition.
import type { ReadonlyDisplayInputs } from './readonly.types';

/** Layout classes that make a field narrower than the row it sits in. */
const WIDTH_FRACTION_PATTERN = /(?:^|:)w-(?:1\/2|1\/3|1\/4|2\/3|3\/4)$/;

/**
 * Answers that need more than one line: they must wrap instead of truncating.
 *
 * Two families, keyed on two different facts. The first is PROSE (`textarea`, `editor`), keyed
 * on the field TYPE — a paragraph read through an ellipsis is not a paragraph.
 *
 * The second is everything that reaches `CompositeDisplayComponent`, keyed on the RESOLVED
 * DISPLAY rather than the type. The seven date/hours composites (task 5, #21239) all resolve to
 * it through the registry, and so does any field that `props.readonlyDisplay: 'composite'`
 * routes there (#21239 follow-up M-1) — `resolveReadonlyDisplay` honours that override BEFORE
 * the type, pinned by `readonly-display.registry.spec.ts` and exercised on a non-composite type
 * in `composite-display.component.spec.ts`. One set on the display covers both, where a type
 * set would need the seven names repeated here AND still miss the override. Its value is what
 * this component already computes as `display()`, so there is no second lookup.
 *
 * Why the whole composite family rather than per instance: the labelled list's rendered LENGTH
 * depends on the stored data, not the type — a filled two-week `availability` schedule is 500+
 * characters, while a `week` answer is one short line (`Week: Week 38, 2026`). The row cannot
 * measure that before it renders. Letting a short composite wrap costs nothing (it still sits on
 * one line), but truncating a long one loses data — `Uren per week: 32 · Oneven weken (Maandag
 * v…` is not one complete day, which is the whole thing the display exists to show.
 *
 * `multiline` does NOT switch layout: `dd-label-list` keeps the same inline grid and only drops
 * `overflow-clip` / `text-ellipsis` / `whitespace-nowrap` (label-list.component.ts), while
 * `items-center` re-centres the label against the taller value.
 */
const MULTILINE_TYPES = new Set(['textarea', 'editor']);
const MULTILINE_DISPLAYS = new Set<Type<unknown>>([CompositeDisplayComponent]);

/**
 * One field as a label/value row in read mode (#21239) — the read counterpart of a field
 * component, mounted by `ReadonlyWrapper`.
 *
 * The row owns the CHROME (label, width, divider) and nothing else: what the answer looks
 * like is the display component the registry resolves for this field
 * (`readonly-display.registry.ts`), projected into the row's value slot. That split is what lets
 * a later type get chips, a thumbnail or a link without this component learning about it — and
 * what keeps the default text rule in one shared place (`formatDisplayValue`) instead of here.
 *
 * Two deliberate differences from the editable field (#21239 feedback round 2026-09-15): the
 * label sits LEFT of the answer (`layout="inline"`), because read mode exists to be scanned; and
 * the field description is not shown — it is fill-in help ("Status wordt meegenomen in de
 * planning"), which belongs to the moment you edit the field, not to reading its answer.
 *
 * Presentational: everything it shows comes from the two inputs.
 */
@Component({
  selector: 'dd-readonly-field-row',
  imports: [LabelListComponent, DdValueDirective, NgComponentOutlet, StatusBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the row adds no box of its own, so `dd-label-list` keeps the exact
  // position in the parent layout that an editable field's host would have had.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    <!-- data-cy on the VALUE cell: the row's stable hook for the read displays and for the
         Storybook plays, which used to reach for the .sentry-mask class — a privacy concern that
         happens to sit on the same element, so renaming it would silently unhook every play. -->
    <dd-label-list
      [label]="label()"
      layout="inline"
      [fullWidth]="fullWidth()"
      [multiline]="multiline()"
      dataCy="readonly-value">
      @if (isMissing()) {
        <!-- Replaces the display 1-on-1 in the value slot (design verdict §D) — same row chrome,
             no extra badge markup or align hack. data-cy is a stable hook independent of the
             badge's own internal one ('status-badge-' + slug-of-label, tied to the Dutch label). -->
        <app-status-badge ddValue label="Ontbreekt" variant="warning" data-cy="readonly-missing" />
      } @else {
        <ng-container ddValue [ngComponentOutlet]="display()" [ngComponentOutletInputs]="displayInputs()" />
      }
    </dd-label-list>
  `,
})
export class ReadonlyFieldRowComponent {
  readonly field = input.required<FormlyFieldConfig>();
  /** Resolved by the wrapper (`readonlyFieldValue`), not read from the control here. */
  readonly value = input<unknown>();

  readonly label = computed(() => this.field().props?.label ?? '');

  /**
   * A REQUIRED field with no answer (task 21245, design spec §4 / verdict §B): the extension
   * hides an empty OPTIONAL field before it ever reaches a row (`extensions/readonly.extension.ts`),
   * so a row that DOES render with nothing in it is always either required-and-empty (this badge)
   * or a field whose `hide` is a module-configured STRING expression the extension deliberately
   * leaves alone — which still has to read as a plain empty answer, hence checking emptiness here
   * too rather than assuming "rendered" already means "answered".
   */
  readonly isMissing = computed(() => !!this.field().props?.required && isEmptyValue(this.value()));

  readonly display = computed(() => resolveReadonlyDisplay(this.field()));

  /**
   * The display's inputs by NAME (`ReadonlyDisplayInputs`). Typed rather than an untyped bag, so
   * renaming an input on the contract breaks here instead of silently rendering nothing.
   */
  readonly displayInputs = computed<ReadonlyDisplayInputs>(() => ({ field: this.field(), value: this.value() }));

  /**
   * A field configured narrower than the row keeps that width; everything else spans the row.
   * Read rows are shorter than inputs, so a full-width default would leave large gaps.
   */
  readonly fullWidth = computed(() =>
    (this.field().className ?? '').split(/\s+/).every(token => !WIDTH_FRACTION_PATTERN.test(token))
  );

  readonly multiline = computed(
    () => MULTILINE_TYPES.has(String(this.field().type)) || MULTILINE_DISPLAYS.has(this.display())
  );
}
